import {
	ref,
	computed,
	onMounted,
	onUnmounted,
	watch,
	type Ref,
	type MaybeRefOrGetter,
	toValue,
} from 'vue';

export interface SlideTimerOptions {
	/** Seconds per slide. */
	durationSeconds: MaybeRefOrGetter<number>;
	/** Start automatically (default true). */
	autoStart?: boolean;
	/** Respect prefers-reduced-motion (default true). */
	respectReducedMotion?: boolean;
	/** Called exactly when a slide should advance. */
	onTick?: () => void;
}

export interface SlideTimerApi {
	isPlaying: Ref<boolean>;
	/** 0..1 progress of the current slide. */
	progress: Ref<number>;
	/** Remaining ms in the current slide. */
	remainingMs: Ref<number>;
	/** Start or resume without resetting progress. */
	play: () => void;
	/** Pause and keep progress. */
	pause: () => void;
	/** Stop, reset progress to 0, keep it paused. */
	stop: () => void;
	/** Reset to start and play from the beginning. */
	restart: () => void;
}

export function useSlideTimer(opts: SlideTimerOptions): SlideTimerApi {
	const autoStart = opts.autoStart ?? true;
	const respectReducedMotion = opts.respectReducedMotion ?? true;

	const durationMs = computed(
		() => Math.max(2, toValue(opts.durationSeconds)) * 1000
	);

	const isPlaying = ref(false);
	const progress = ref(0); // 0..1
	const remainingMs = ref(durationMs.value);

	let timeoutId: number | undefined;
	let rafId: number | undefined;
	let cycleStartTs = 0;

	function clearTimeoutSafe() {
		if (timeoutId !== undefined) {
			clearTimeout(timeoutId);
			timeoutId = undefined;
		}
	}
	function cancelRaf() {
		if (rafId !== undefined) {
			cancelAnimationFrame(rafId);
			rafId = undefined;
		}
	}

	function stepRaf() {
		if (typeof performance === 'undefined') return;
		const now = performance.now();
		const d = durationMs.value || 1;
		const elapsed = now - cycleStartTs;
		progress.value = Math.min(1, Math.max(0, elapsed / d));
		if (isPlaying.value) rafId = requestAnimationFrame(stepRaf);
	}

	function scheduleNextTick(ms: number) {
		const d = durationMs.value;
		const now =
			typeof performance !== 'undefined' ? performance.now() : Date.now();
		remainingMs.value = Math.max(0, ms);
		cycleStartTs = now - Math.max(0, d - remainingMs.value);

		clearTimeoutSafe();
		timeoutId = window.setTimeout(() => {
			progress.value = 0;
			remainingMs.value = d;
			opts.onTick?.();
			scheduleNextTick(d);
		}, Math.max(0, ms));

		cancelRaf();
		rafId = requestAnimationFrame(stepRaf);
	}

	function pause() {
		if (!isPlaying.value) return;
		isPlaying.value = false;
		const d = durationMs.value;
		remainingMs.value = Math.max(0, (1 - progress.value) * d);
		clearTimeoutSafe();
		cancelRaf();
	}

	function play() {
		if (isPlaying.value) return;
		isPlaying.value = true;
		scheduleNextTick(remainingMs.value || durationMs.value);
	}

	function stop() {
		isPlaying.value = false;
		clearTimeoutSafe();
		cancelRaf();
		progress.value = 0;
		remainingMs.value = durationMs.value;
	}

	function restart() {
		stop();
		play();
	}

	watch(durationMs, (newD, _oldD) => {
		// Recompute remaining from current progress ratio
		remainingMs.value = Math.max(0, (1 - progress.value) * newD);
		if (isPlaying.value) scheduleNextTick(remainingMs.value);
	});

	onMounted(() => {
		const prefersReduced =
			respectReducedMotion &&
			typeof window !== 'undefined' &&
			window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

		remainingMs.value = durationMs.value;
		if (autoStart && !prefersReduced) play();
	});

	onUnmounted(() => {
		clearTimeoutSafe();
		cancelRaf();
	});

	return { isPlaying, progress, remainingMs, play, pause, stop, restart };
}
